import React from "react";
import { getLocale, t } from "@auto/services";
import * as echarts from "echarts";

const numericLabelValue = (value) => {
  if (Array.isArray(value)) {
    for (let index = value.length - 1; index >= 0; index -= 1) {
      const number = Number(value[index]);
      if (Number.isFinite(number)) return number;
    }
    return null;
  }

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatValueLabel = (params) => {
  const value = numericLabelValue(params?.value);
  if (value === null) return "";

  try {
    return new Intl.NumberFormat(getLocale()).format(value);
  } catch {
    return String(value);
  }
};

const withVisibleValueLabels = (option) => {
  if (!option || !Array.isArray(option.series)) return option;

  const xAxis = Array.isArray(option.xAxis) ? option.xAxis[0] : option.xAxis;
  const yAxis = Array.isArray(option.yAxis) ? option.yAxis[0] : option.yAxis;
  const horizontalBar = xAxis?.type === "value" && yAxis?.type === "category";

  return {
    ...option,
    series: option.series.map((series) => {
      if (!series || !["bar", "line", "pie", "heatmap", "map"].includes(series.type)) {
        return series;
      }

      const position = series.type === "pie"
        ? "outside"
        : series.type === "heatmap" || series.type === "map"
          ? "inside"
          : series.type === "bar" && horizontalBar
            ? "right"
            : "top";

      return {
        ...series,
        label: {
          ...(series.label || {}),
          show: true,
          position: series.label?.position || position,
          formatter: series.type === "pie"
            ? (params) => {
                const value = formatValueLabel(params);
                return value ? `${params.name}: ${value}` : "";
              }
            : formatValueLabel,
        },
        labelLayout: {
          ...(series.labelLayout || {}),
          hideOverlap: false,
        },
      };
    }),
  };
};

const EChart = ({
  option,
  loading = false,
  onClick = null,
  className = "",
  style = null,
}) => {
  const hostRef = React.useRef(null);
  const chartRef = React.useRef(null);

  React.useEffect(() => {
    if (!hostRef.current) return undefined;

    const chart = echarts.init(hostRef.current, null, { renderer: "canvas" });
    chartRef.current = chart;

    const resize = () => chart.resize();
    const observer = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(resize)
      : null;

    if (observer) observer.observe(hostRef.current);
    else window.addEventListener("resize", resize);

    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener("resize", resize);
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !option) return;
    chart.setOption(withVisibleValueLabels(option), { notMerge: true, lazyUpdate: false });
  }, [option]);

  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return undefined;

    chart.off("click");
    if (onClick) chart.on("click", onClick);

    return () => chart.off("click");
  }, [onClick]);

  React.useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    if (loading) {
      chart.showLoading("default", {
        text: t("common.loading.short"),
        maskColor: "rgba(255, 255, 255, 0.72)",
      });
    } else {
      chart.hideLoading();
    }
  }, [loading]);

  return <div ref={hostRef} className={className} style={style} />;
};

export default EChart;
